import { Link } from "react-router-dom";
import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import { useEffect, useRef } from "react";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight, Quote } from "lucide-react";

/* ---------- Sub-components ---------- */

function FadeUp({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function Counter({ from = 0, to, prefix = "", suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const mv = useMotionValue(from);
  const rounded = useTransform(mv, (v) => `${prefix}${Math.round(v).toLocaleString("en-IN")}${suffix}`);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, to, { duration: 2.2, ease: [0.22, 1, 0.36, 1] });
    return () => controls.stop();
  }, [inView, mv, to]);

  return <motion.span ref={ref}>{rounded}</motion.span>;
}

/* ---------- Sections ---------- */

const HERO_IMG = "https://images.pexels.com/photos/8837758/pexels-photo-8837758.jpeg?auto=compress&cs=tinysrgb&w=2000&q=80";

function Hero() {
  return (
    <section data-testid="hero-section" className="relative min-h-[100svh] w-full overflow-hidden">
      <img
        src={HERO_IMG}
        alt="Indian woman micropreneur at work"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 hero-overlay" />
      <div className="absolute inset-0 bg-grain" />

      <div className="relative container-saksham h-[100svh] flex flex-col justify-end pb-20 md:pb-28">
        <FadeUp delay={0.1}>
          <h1 className="font-serif text-white text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight font-medium max-w-5xl">
            Empowering <em className="text-[#FBD223] not-italic">India&apos;s</em> <br className="hidden md:block" />
            <span className="italic font-light">Micro</span>preneurs
          </h1>
        </FadeUp>
        <FadeUp delay={0.25}>
          <p className="mt-8 max-w-2xl text-base md:text-lg text-white/85 leading-relaxed">
            Bridging the gap between ambition and achievement through practical consulting,
            knowledge, and support for <span className="text-[#FBD223] font-medium">12 million women-owned enterprises</span> across India.
          </p>
        </FadeUp>
        <FadeUp delay={0.4}>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/what-we-do"
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 bg-[#FBD223] text-[#0A0A0A] hover:bg-white px-7 py-4 text-sm font-medium transition-colors"
            >
              Explore Our Work <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 border border-white/40 text-white hover:bg-white hover:text-[#0A0A0A] px-7 py-4 text-sm font-medium transition-colors"
            >
              Connect
            </Link>
          </div>
        </FadeUp>
      </div>

      <div className="absolute bottom-6 right-6 hidden md:flex items-center gap-3 text-white/60 text-xs">
        <span className="w-12 h-px bg-white/40" />
        <span className="tracking-[0.2em] uppercase">Scroll</span>
      </div>
    </section>
  );
}

function Vision() {
  return (
    <section data-testid="vision-section" className="bg-[#FAFAFA] section-y border-b border-black/5">
      <div className="container-saksham">
        <FadeUp>
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#09B792] font-semibold mb-8">Our Vision</p>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight font-medium text-[#0A0A0A] max-w-5xl">
            Driving <em className="text-[#09B792] not-italic">inclusive growth</em> by{" "}
            <span className="italic">democratising</span> success.
          </h2>
        </FadeUp>
      </div>
    </section>
  );
}

function About() {
  return (
    <section data-testid="about-section" className="bg-[#F3F1ED] section-y">
      <div className="container-saksham grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-5">
          <FadeUp>
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#09B792] font-semibold mb-6">About Saksham</p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight font-medium">
              Unlocking India&apos;s <span className="italic">untapped</span> entrepreneurial potential.
            </h2>
          </FadeUp>
        </div>
        <div className="md:col-span-7 md:pl-8">
          <FadeUp delay={0.2}>
            <p className="text-lg md:text-xl leading-relaxed text-[#0A0A0A]/85 font-light">
              India&apos;s entrepreneurial landscape is transforming, with{" "}
              <span className="text-[#0A0A0A] font-medium">12 million women-owned enterprises</span> among the nation&apos;s
              63 million MSMEs. Yet beneath these numbers lies immense untapped potential. Many micropreneurs,
              particularly women, navigate their journey without access to formal finance, digital tools, or the
              networks that could accelerate their growth.
            </p>
          </FadeUp>
          <FadeUp delay={0.35}>
            <div className="mt-10 flex flex-wrap gap-6">
              <div className="border-l-2 border-[#FBD223] pl-4">
                <div className="font-serif text-3xl font-medium">63M</div>
                <div className="text-xs uppercase tracking-widest text-[#4A4A4A] mt-1">MSMEs in India</div>
              </div>
              <div className="border-l-2 border-[#09B792] pl-4">
                <div className="font-serif text-3xl font-medium">12M</div>
                <div className="text-xs uppercase tracking-widest text-[#4A4A4A] mt-1">Women-Owned</div>
              </div>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

const FOCUS_AREAS = [
  {
    title: "Finance Access",
    desc: "Navigating MUDRA, Stand-Up India, PMEGP and beyond.",
    image: "https://images.pexels.com/photos/8576784/pexels-photo-8576784.jpeg?auto=compress&cs=tinysrgb&w=1400&q=80",
    testid: "focus-finance",
  },
  {
    title: "Strategic Marketing",
    desc: "Low-cost visibility that builds brand without burning capital.",
    image: "https://images.pexels.com/photos/33765890/pexels-photo-33765890.jpeg?auto=compress&cs=tinysrgb&w=1400&q=80",
    testid: "focus-marketing",
  },
  {
    title: "Digital Tools",
    desc: "Practical, everyday tech that quietly transforms operations.",
    image: "https://images.pexels.com/photos/8837744/pexels-photo-8837744.jpeg?auto=compress&cs=tinysrgb&w=1400&q=80",
    testid: "focus-digital",
  },
];

function FocusAreas() {
  return (
    <section data-testid="focus-areas-section" className="bg-[#FAFAFA] section-y">
      <div className="container-saksham">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <FadeUp>
              <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#09B792] font-semibold mb-4">Our Focus Areas</p>
            </FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-medium max-w-3xl">
                Three pillars. <span className="italic">One mission.</span>
              </h2>
            </FadeUp>
          </div>
          <FadeUp delay={0.2}>
            <Link
              to="/what-we-do"
              data-testid="focus-view-all"
              className="inline-flex items-center gap-2 text-sm font-medium link-underline text-[#0A0A0A]"
            >
              See how we work <ArrowRight size={16} />
            </Link>
          </FadeUp>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {FOCUS_AREAS.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <Link
                to="/what-we-do"
                data-testid={f.testid}
                className="group block bg-white border border-black/5 hover:border-[#09B792]/40 transition-all duration-300"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-[#F3F1ED]">
                  <img
                    src={f.image}
                    alt={f.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 bg-[#FBD223] text-[#0A0A0A] text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1">
                    0{i + 1}
                  </div>
                </div>
                <div className="p-7">
                  <h3 className="font-serif text-2xl md:text-3xl font-medium leading-tight">{f.title}</h3>
                  <p className="mt-3 text-[#4A4A4A] text-sm leading-relaxed">{f.desc}</p>
                  <span className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-[#09B792] group-hover:gap-3 transition-all">
                    Learn more <ArrowUpRight size={16} />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const METRICS = [
  { label: "Micropreneurs Empowered", to: 10, suffix: "+" },
  { label: "Business Cost Saved", to: 50000, prefix: "₹", suffix: "+" },
  { label: "Admin Hours Reclaimed", to: 80, suffix: "+", unit: "Hrs" },
];

function Impact() {
  return (
    <section data-testid="impact-section" className="bg-[#0A0A0A] text-white section-y relative overflow-hidden">
      <div className="bg-grain absolute inset-0" />
      <div className="container-saksham relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-6">
            <FadeUp>
              <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#FBD223] font-semibold mb-4">Our Reach</p>
            </FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-medium">
                Impact at a <em className="text-[#FBD223] not-italic">glance.</em>
              </h2>
            </FadeUp>
          </div>
          <div className="md:col-span-6 md:pl-8 flex items-end">
            <FadeUp delay={0.2}>
              <p className="text-white/65 text-base md:text-lg leading-relaxed max-w-md">
                Small numbers. Big lives changed. Every metric below represents a real business.
              </p>
            </FadeUp>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {METRICS.map((m, i) => (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              data-testid={`impact-metric-${i}`}
              className="bg-[#0A0A0A] p-10 md:p-14"
            >
              <div className="font-serif text-6xl md:text-7xl lg:text-8xl font-medium tracking-tight text-[#FBD223] leading-none flex items-baseline gap-2">
                <Counter to={m.to} prefix={m.prefix || ""} suffix={m.suffix || ""} />
                {m.unit && (
                  <span className="font-serif text-2xl md:text-3xl lg:text-4xl font-medium text-[#FBD223]/80 tracking-tight">
                    {m.unit}
                  </span>
                )}
              </div>
              <div className="mt-6 text-xs uppercase tracking-[0.25em] text-white/70">{m.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SDGLayer() {
  const sdgs = [
    "SDG 5 — Gender Equality",
    "SDG 8 — Decent Work & Economic Growth",
    "SDG 9 — Industry, Innovation & Infrastructure",
    "SDG 10 — Reduced Inequalities",
  ];
  return (
    <section data-testid="sdg-section" className="bg-[#F3F1ED] py-16 border-y border-black/5 overflow-hidden">
      <div className="container-saksham mb-8">
        <FadeUp>
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#09B792] font-semibold">Aligned With</p>
        </FadeUp>
      </div>
      <Marquee gradient={false} speed={28} pauseOnHover>
        {sdgs.concat(sdgs).map((s, i) => (
          <span key={i} className="sdg-marquee-text text-3xl md:text-5xl mx-12 whitespace-nowrap">
            {s} <span className="text-[#FBD223] mx-6">✦</span>
          </span>
        ))}
      </Marquee>
      <div className="container-saksham mt-10">
        <p className="max-w-3xl text-[#4A4A4A] text-base md:text-lg leading-relaxed">
          Working toward SDG 5, 8, 9 and 10, we aim to close the gap between ambition and achievement — through
          practical consulting, knowledge, and on-the-ground support.
        </p>
      </div>
    </section>
  );
}

const TESTIMONIALS = [
  {
    quote:
      "Saksham designed marketing material for my cafe that finally got people to notice us. Footfall picked up within weeks — a real difference for a small place like mine.",
    name: "Raju",
    role: "Small Cafe Owner",
    place: "New Delhi",
    image: "/raju.webp",
  },
  {
    quote:
      "From taking orders on WhatsApp Business to showcasing my full portfolio there — it changed how customers discover me. My reach has grown far beyond my neighbourhood.",
    name: "Sarita Nagar",
    role: "Boutique Owner",
    place: "New Delhi",
    image: "/sarita-boutique.jpg",
  },
  {
    quote:
      "Saksham helped me understand the MUDRA loan process — clear, patient, no jargon.",
    name: "Shubhi Kumari",
    role: "Handicraft Artisan",
    place: "Lucknow, UP",
    image: "https://images.pexels.com/photos/15815339/pexels-photo-15815339.jpeg",
  },
];

function Testimonials() {
  return (
    <section data-testid="testimonials-section" className="bg-[#FAFAFA] section-y">
      <div className="container-saksham">
        <div className="mb-16">
          <FadeUp>
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#09B792] font-semibold mb-4">Stories of Change</p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-medium max-w-3xl">
              Voices from <span className="italic">the ground.</span>
            </h2>
          </FadeUp>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {TESTIMONIALS.map((t, i) => (
            <motion.article
              key={t.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              data-testid={`testimonial-${i}`}
              className="bg-white border border-black/5 p-8 md:p-10 flex flex-col"
            >
              <Quote size={28} className="text-[#FBD223] mb-6" />
              <p className="font-serif text-lg md:text-xl leading-relaxed text-[#0A0A0A] flex-grow">
                &ldquo;{t.quote}&rdquo;
              </p>
              <div className="mt-8 pt-6 border-t border-black/10 flex items-center gap-4">
                <img
                  src={t.image}
                  alt={t.name}
                  className="w-12 h-12 rounded-full object-cover bg-[#F3F1ED]"
                />
                <div>
                  <div className="font-medium text-sm">{t.name}</div>
                  <div className="text-xs text-[#4A4A4A]">{t.role} · {t.place}</div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

function ClosingCTA() {
  return (
    <section data-testid="closing-cta" className="bg-[#FBD223] section-y">
      <div className="container-saksham flex flex-col md:flex-row md:items-end justify-between gap-8">
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-medium max-w-3xl">
          Have a story to share, or want to <span className="italic">walk alongside us?</span>
        </h2>
        <Link
          to="/contact"
          data-testid="closing-cta-button"
          className="inline-flex items-center gap-2 bg-[#0A0A0A] text-[#FBD223] hover:bg-white hover:text-[#0A0A0A] px-8 py-4 text-sm font-medium transition-colors whitespace-nowrap"
        >
          Reach Out <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="page-home">
      <Hero />
      <Vision />
      <About />
      <FocusAreas />
      <Impact />
      <SDGLayer />
      <Testimonials />
      <ClosingCTA />
    </div>
  );
}
